import { IconButton } from "@itk/mui-components";
import { useSnackbar } from "notistack";
import { useCallback, useEffect } from "react";
import { connect } from "react-redux";
import { RootState } from "src/store";
import { isApiError, isFrontendError, isItkException, isLoginError } from "src/utils/error-utils";
import { errorActions, ItkError } from "./error-store";
import { useItkException } from "./use-itk-exception";
import { DekoxIcon } from "../deko-x-icons";

import { useFrontendErrorTranslation } from "./frontend-error.i18n";
import { useLoginErrorTranslation } from "./login-error";

const mapStateToProps = (state: RootState) => ({ ...state.generalReducer.errorReducer });

type Props = ReturnType<typeof mapStateToProps> & typeof errorActions;

type EnqueueErrorParams = { message: string; persist?: boolean };

const ErrorViewerBase = (props: Props) => {
  const { errors, removeError } = props;
  const { getItkExceptionMessage } = useItkException();
  const { getFrontendErrorTranslation } = useFrontendErrorTranslation();
  const { getLoginErrorTranslation } = useLoginErrorTranslation();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const mapErrorToEnqueueErrorParams = useCallback(
    (error: ItkError): EnqueueErrorParams => {
      if (typeof error.error === "string") {
        return { message: error.error };
      } else if (isApiError(error.error) && isItkException(error.error.body)) {
        const message = getItkExceptionMessage(error.error.body);

        return { message };
      } else if (isItkException(error.error)) {
        const message = getItkExceptionMessage(error.error);

        return { message };
      } else if (isFrontendError(error.error)) {
        const message = getFrontendErrorTranslation(error.error.statusCode);

        return { message };
      } else if (isLoginError(error.error)) {
        const message = getLoginErrorTranslation(error.error);

        return { message };
      }

      // eslint-disable-next-line @itk/undesired-code-alerter
      console.log({ error: error.error }); // This console.log is allowed
      return { message: "Unexpected error occured" };
    },
    [getItkExceptionMessage]
  );

  useEffect(() => {
    for (const error of errors) {
      const enqueueErrorParams = mapErrorToEnqueueErrorParams(error);
      enqueueSnackbar(enqueueErrorParams.message, {
        key: error.id,
        style: { whiteSpace: "pre-line" },
        variant: "warning",
        preventDuplicate: true,
        persist: enqueueErrorParams.persist ?? false,
        onClose: () => removeError(error.id),
        action: (
          <IconButton k={error.id} onClick={() => closeSnackbar(error.id)} color="inherit">
            <DekoxIcon icon="close" />
          </IconButton>
        )
      });
    }
  }, [errors, enqueueSnackbar, closeSnackbar]);

  return null;
};

export const ErrorViewer = connect(mapStateToProps, errorActions)(ErrorViewerBase);
